import type { FC } from "react"
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from "@mui/material"
import { DateCalendar, MultiSectionDigitalClock } from "@mui/x-date-pickers"
import type { DateTime } from "luxon"

import RangeDay from "./DateRangeEditor/RangeDay"
import { DAY_RING_WIDTH } from "./DateRangeEditor/range"
import { PANEL_LINE } from "./DateRangeEditor/constants"

/** Tighter rows than MUI's 40px, so six weeks take less height in the popover. */
const DAY_ROW_HEIGHT = 36
const WEEKDAY_LABEL_HEIGHT = 32
/** Six weeks, with room above and below for the ring of a day in the first or last one. */
const WEEKS_HEIGHT = 6 * DAY_ROW_HEIGHT + 2 * DAY_RING_WIDTH
/** Header (56) + weekday labels + the weeks, down from MUI's 336; the clock beside it matches. */
const CALENDAR_HEIGHT = 56 + WEEKDAY_LABEL_HEIGHT + WEEKS_HEIGHT
/** Under the calendar on a phone, the clock shows about three rows. */
const STACKED_CLOCK_HEIGHT = 120
const CLOCK_UNITS = ["HH", "MM"]
const CLOCK_ITEM_WIDTH = 44
/** MUI's year rows: a 36px button with 6px above and below. */
const YEAR_ROW_HEIGHT = 48
const VISIBLE_YEAR_ROWS = 5

/** Scrolls without showing a scrollbar, like the clock's columns. */
const hiddenScrollbarSx = {
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
} as const

const pickerPanelSx = {
  border: 1,
  borderColor: PANEL_LINE,
  borderRadius: 2,
  backgroundColor: "background.default",
  overflow: "hidden",
}

interface DateTimePanelProps {
  value: DateTime | null
  onChange: (date: DateTime | null) => void
  disableFuture: boolean
  minDate?: DateTime
  maxDate?: DateTime
}

/**
 * The calendar and the clock that edit one moment, side by side in a bordered panel, or
 * as two panels one above the other on small screens. Inside a RangeContext the calendar
 * paints that range around the moment.
 */
const DateTimePanel: FC<DateTimePanelProps> = ({
  value,
  onChange,
  disableFuture,
  minDate,
  maxDate,
}) => {
  const theme = useTheme()
  const isMediumOrSmallDevice = useMediaQuery(theme.breakpoints.down("md"))
  const clockHeight = isMediumOrSmallDevice ? STACKED_CLOCK_HEIGHT : "none"

  const calendar = (
    <DateCalendar
      value={value}
      onChange={onChange}
      disableFuture={disableFuture}
      maxDate={maxDate}
      minDate={minDate}
      // Outside a range it draws no band, but its wrapper still keeps the day round.
      slots={{ day: RangeDay }}
      sx={{
        m: 0,
        flexShrink: 0,
        ...(!isMediumOrSmallDevice && {
          height: CALENDAR_HEIGHT,
          "& .MuiDayCalendar-weekContainer": { my: 0 },
          "& .MuiDayCalendar-weekDayLabel": { height: WEEKDAY_LABEL_HEIGHT },
          // MUI reserves six weeks of its own row height.
          "& .MuiDayCalendar-slideTransition": { minHeight: WEEKS_HEIGHT },
        }),
        // The month slides inside a box that clips, so the edge weeks need room for the ring.
        "& .MuiDayCalendar-monthContainer": { py: `${DAY_RING_WIDTH}px` },
        // MUI's 280px of years runs past the calendar, hiding the last row and dragging the
        // header up when it scrolls to the selected year.
        "& .MuiYearCalendar-root": {
          maxHeight: VISIBLE_YEAR_ROWS * YEAR_ROW_HEIGHT,
          ...hiddenScrollbarSx,
          // On a phone it spans the calendar's full width, like the days.
          ...(isMediumOrSmallDevice && { width: "100%" }),
        },
        // On a phone the days spread over the whole width instead of MUI's 320px, with
        // bigger text. Each day takes a seventh of the row, so the range's band runs
        // unbroken and the weekday labels stay over their days.
        ...(isMediumOrSmallDevice && {
          width: "100%",
          "& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer": { px: 1 },
          "& .MuiDayCalendar-weekDayLabel, & .MuiDayCalendar-weekContainer > *": {
            flex: 1,
          },
          "& .MuiDayCalendar-weekContainer > *": {
            display: "flex",
            justifyContent: "center",
          },
          // At most MUI's 36px, and as tall as wide, so a narrow row shrinks the circle
          // instead of squeezing it into an oval.
          "& .MuiPickersDay-root": {
            fontSize: "0.875rem",
            width: "100%",
            maxWidth: 36,
            height: "auto",
            aspectRatio: "1",
          },
        }),
      }}
    />
  )
  const clock = (
    <Stack
      sx={{
        flex: 1,
        minWidth: 0,
        // The HH/MM header counts too, so the column is no taller than the calendar.
        ...(!isMediumOrSmallDevice && { height: CALENDAR_HEIGHT }),
      }}
    >
      <Stack
        direction="row"
        divider={
          <Divider orientation="vertical" flexItem sx={{ borderColor: PANEL_LINE }} />
        }
      >
        {CLOCK_UNITS.map((unit) => (
          <Typography
            key={unit}
            variant="overline"
            align="center"
            sx={{ flex: 1, py: 1 }}
          >
            {unit}
          </Typography>
        ))}
      </Stack>
      <MultiSectionDigitalClock
        value={value}
        onChange={onChange}
        views={["hours", "minutes"]}
        ampm={false}
        disableFuture={disableFuture}
        // The field's bounds, whole moments: by default MUI applies them to every day.
        disableIgnoringDatePartForTimeValidation
        minTime={minDate}
        maxTime={maxDate}
        // MUI sizes the clock for a popup of its own; here it fills its panel.
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          maxHeight: clockHeight,
          border: 0,
          "& .MuiMultiSectionDigitalClockSection-root": {
            flex: 1,
            maxHeight: clockHeight,
            // MUI pads the end so the last time can scroll up to the top; stop at the last one.
            "&::after": { display: "none" },
          },
          "& .MuiMultiSectionDigitalClockSection-root:not(:first-of-type)": {
            borderLeftColor: PANEL_LINE,
          },
          // One pill size for every time, centred in its column with room around the digits.
          "& .MuiMultiSectionDigitalClockSection-item": {
            width: CLOCK_ITEM_WIDTH,
            mx: "auto",
            justifyContent: "center",
            ...(isMediumOrSmallDevice && {
              // MenuItem's 48px touch height on phones is far taller than a time needs.
              minHeight: "auto",
              py: 0.75,
              fontSize: "0.875rem",
            }),
          },
        }}
      />
    </Stack>
  )

  return isMediumOrSmallDevice ? (
    <Stack spacing={2}>
      <Box sx={pickerPanelSx}>{calendar}</Box>
      <Box sx={pickerPanelSx}>{clock}</Box>
    </Stack>
  ) : (
    <Stack
      direction="row"
      divider={
        <Divider orientation="vertical" flexItem sx={{ borderColor: PANEL_LINE }} />
      }
      sx={pickerPanelSx}
    >
      {calendar}
      {clock}
    </Stack>
  )
}

export default DateTimePanel
